/**
 * Wraps a synchronous function call in a try-catch block and returns a tuple of [result, error].
 * It's a slightly modified version of the gist by @t3dotgg:
 *
 * @template T The type of the successful result.
 * @template E The type of the error (defaults to `Error`).
 * @param fn A function to execute.
 * @returns A tuple: `[result, null]` if successful, or `[null, error]` if an error was thrown.
 * @copyright https://gist.github.com/t3dotgg/a486c4ae66d32bf17c09c73609dacc5b
 */

import { Result } from './safe-call.types';

export function safeCall<T, E = Error>(fn: () => T): Result<T, E> {
  try {
    const result = fn();
    return [result, null];
  } catch (error) {
    return [null, error as E];
  }
}

/**
 * Wraps an asynchronous operation in a try-catch block and returns a tuple of [result, error].
 * It's a slightly modified version of the gist by @t3dotgg:
 *
 * @template T The type of the successful result.
 * @template E The type of the error (defaults to `Error`).
 * @param promise A promise to execute.
 * @returns A tuple: `[result, null]` if successful, or `[null, error]` if an error was thrown.
 * @copyright https://gist.github.com/t3dotgg/a486c4ae66d32bf17c09c73609dacc5b
 */

export async function safeCallAsync<T, E = Error>(
  promise: Promise<T>
): Promise<Result<T, E>> {
  try {
    const result = await promise;
    return [result, null];
  } catch (error) {
    return [null, error as E];
  }
}
